// src/services/ShareStateService.ts
import pako from 'pako';
import { mods as modsV2 } from '../data/mods';
import { mods as modsV1 } from '../data/modsx';

// Define interfaces for data structures
interface AtlasDeviceState {
  mod: string;
  selections: Array<{id: string; scarabId: string}>;
  influence: string;
  maps: string[];
}

interface OptimizedAtlasState {
  m: string;
  s: Array<{i: string; s: string}>;
  f: string;   // influence
  p: string[]; // maps
}

interface StrategyState {
  name: string;
  difficulty: string;
  cost: string;
  description: string;
}

interface OptimizedStrategyState {
  n: string;
  d: string;
  c: string;
  t: string;
}

interface IdolStat {
  value: number;
  contentType: string;
  explicitType: string;
  modId: number;
}

interface OptimizedIdolStat {
  v: number;
  c: string;
  e: string;
  i: number;
}

interface IdolItem {
  id: string;
  name: string;
  size: {
    width: number;
    height: number;
    color: string;
    name: string;
  };
  stats: IdolStat[];
  position: {row: number; col: number} | null;
}

interface OptimizedIdolItem {
  i: string;
  n: string;
  t: {
    w: number;
    h: number;
    c: string;
    n: string;
  };
  s: OptimizedIdolStat[];
  p: {r: number; c: number} | null;
}

interface SharePayload {
  v: number;
  t: number;
  s: Record<string, unknown>;
}

// Define the state keys we want to share
const STORAGE_KEYS = [
  'atlasDeviceState',
  'atlasStrategy', 
  'idolPlannerItems',
  'idolStashItems',
  'pinnedContentType',
  'pinnedContentTypes',
];

/**
 * Migrate mod IDs from v1 to v2
 * Looks up old mod by ID in modsx.ts, then finds matching mod in mods.ts by tradeId and idolSizes
 * Also adjusts values if they're out of bounds for the new mod
 */
function migrateModId(oldModId: number, value: number, stats: { mapped: number; unmapped: number; unmappedIds: number[]; mappings: Array<{oldId: number, newId: number, oldDesc: string, newDesc: string}> }): { newId: number; newValue: number } {
  // Find the old mod
  const oldMod = Object.values(modsV1).find(mod => mod.id === oldModId);
  
  if (!oldMod) {
    stats.unmapped++;
    stats.unmappedIds.push(oldModId);
    return { newId: oldModId, newValue: value }; // Return original if not found
  }
  
  // Find matching new mod by tradeId and idolSizes
  const newMod = Object.values(modsV2).find(mod => {
    // Match by tradeId (if available) and idolSizes
    const tradeIdMatch = oldMod.tradeId && mod.tradeId === oldMod.tradeId;
    const idolSizesMatch = JSON.stringify(mod.idolSizes?.sort()) === JSON.stringify(oldMod.idolSizes?.sort());
    
    return tradeIdMatch && idolSizesMatch;
  });
  
  if (newMod) {
    stats.mapped++;
    stats.mappings.push({
      oldId: oldModId,
      newId: newMod.id,
      oldDesc: oldMod.description || '',
      newDesc: newMod.description || ''
    });
    
    // Check if value needs adjustment
    let adjustedValue = value;
    if (newMod.range && newMod.range.length === 2) {
      const [min, max] = newMod.range;
      if (value < min || value > max) {
        // Value is out of bounds, set to mid value
        adjustedValue = Math.round((min + max) / 2);
      }
    }
    
    return { newId: newMod.id, newValue: adjustedValue };
  }
  
  stats.unmapped++;
  stats.unmappedIds.push(oldModId);
  return { newId: oldModId, newValue: value }; // Return original if no match found
}

/**
 * Migrate idol items from v1 to v2
 */
function migrateIdolItems(items: OptimizedIdolItem[], stats: { mapped: number; unmapped: number; unmappedIds: number[]; mappings: Array<{oldId: number, newId: number, oldDesc: string, newDesc: string}> }): OptimizedIdolItem[] {
  if (!items) return [];
  
  return items.map(item => ({
    ...item,
    s: item.s.map(stat => {
      const { newId, newValue } = migrateModId(stat.i, stat.v, stats);
      return {
        ...stat,
        i: newId,
        v: newValue
      };
    })
  }));
}

/**
 * Encoding & Decoding utilities
 */
function uint8ToBase64(uint8Array: Uint8Array) {
  let binary = '';
  for (let i = 0; i < uint8Array.length; i++) {
    binary += String.fromCharCode(uint8Array[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-') // URL safe
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function base64ToUint8Array(base64: string) {
  base64 = base64.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4;
  if (pad) {
    base64 += '='.repeat(4 - pad);
  }
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

/**
 * Optimize Atlas Device State
 */
function optimizeAtlasDeviceState(data: string | AtlasDeviceState): OptimizedAtlasState | null {
  if (!data) return null;
  
  try {
    const parsed: AtlasDeviceState = typeof data === 'string' ? JSON.parse(data) : data;
    
    return {
      m: parsed.mod,
      s: parsed.selections.map((sel) => ({
        i: sel.id,
        s: sel.scarabId
      })),
      f: parsed.influence || '',
      p: parsed.maps || []
    };
  } catch (err) {
    console.error('Failed to optimize Atlas Device State:', err);
    return null;
  }
}

/**
 * Optimize Strategy
 */
function optimizeStrategy(data: string | StrategyState): OptimizedStrategyState | null {
  if (!data) return null;
  
  try {
    const parsed: StrategyState = typeof data === 'string' ? JSON.parse(data) : data;
    
    return {
      n: parsed.name,
      d: parsed.difficulty,
      c: parsed.cost,
      t: parsed.description
    };
  } catch (err) {
    console.error('Failed to optimize Strategy:', err);
    return null;
  }
}

/**
 * Optimize Idol Items
 */
function optimizeIdolItems(data: string | IdolItem[]): OptimizedIdolItem[] | null {
  if (!data) return null;
  
  try {
    const parsed: IdolItem[] = typeof data === 'string' ? JSON.parse(data) : data;
    
    // Filter out null items instead of returning null
    return parsed.filter(item => item !== null).map((item) => {
      // Create a minimized version of each idol
      return {
        i: item.id,
        n: item.name,
        t: {
          w: item.size.width,
          h: item.size.height,
          c: item.size.color,
          n: item.size.name
        },
        s: item.stats.map((stat) => ({
          v: stat.value,
          c: stat.contentType,
          e: stat.explicitType,
          i: stat.modId
        })),
        p: item.position ? {
          r: item.position.row,
          c: item.position.col
        } : null
      };
    });
  } catch (err) {
    console.error('Failed to optimize Idol Items:', err);
    return null;
  }
}

/**
 * Restore Atlas Device State
 */
function restoreAtlasDeviceState(data: OptimizedAtlasState): AtlasDeviceState {
  if (!data) return { mod: '', selections: [], influence: '', maps: [] };
  
  return {
    mod: data.m,
    selections: data.s.map((sel: {i: string; s: string}) => ({
      id: sel.i,
      scarabId: sel.s
    })),
    influence: data.f || '',
    maps: data.p || []
  };
}

function restoreStrategy(data: OptimizedStrategyState): StrategyState {
  if (!data) return { name: '', difficulty: '', cost: '', description: '' };
  
  return {
    name: data.n,
    difficulty: data.d,
    cost: data.c,
    description: data.t
  };
}

/**
 * Restore Idol Items
 */
function restoreIdolItems(data: OptimizedIdolItem[]): IdolItem[] {
  if (!data) return [];
  
  // Filter out null items
  return data.filter(item => item !== null).map((item: OptimizedIdolItem) => {
    return {
      id: item.i,
      name: item.n,
      size: {
        width: item.t.w,
        height: item.t.h,
        color: item.t.c,
        name: item.t.n
      },
      stats: item.s.map((stat: OptimizedIdolStat) => ({
        value: stat.v,
        modId: stat.i,
        contentType: stat.c,
        explicitType: stat.e
      })),
      position: item.p ? {
        row: item.p.r,
        col: item.p.c
      } : null
    };
  });
}

const ShareStateService = {
  /**
   * Creates a shareable URL containing the current application state
   */
  createShareableUrl(): string {
    try {
      // Collect and optimize all state from localStorage
      const optimizedState: Record<string, unknown> = {};
      
      for (const key of STORAGE_KEYS) {
        const value = localStorage.getItem(key);
        if (!value) continue;
        
        try {
          if (key === 'atlasDeviceState') {
            optimizedState.a = optimizeAtlasDeviceState(value);
          } else if (key === 'atlasStrategy') {
            optimizedState.b = optimizeStrategy(value);
          } else if (key === 'idolPlannerItems') {
            optimizedState.c = optimizeIdolItems(value);
          } else if (key === 'idolStashItems') {
            optimizedState.d = optimizeIdolItems(value);
          } else if (key === 'pinnedContentType') {
            optimizedState.e = JSON.parse(value);
          } else if (key === 'pinnedContentTypes') {
            optimizedState.f = JSON.parse(value);
          }
        } catch (err) {
          console.error(`Failed to process ${key}:`, err);
        }
      }
      
      // Add version and timestamp
      const payload: SharePayload = {
        v: 2, // Updated to version 2
        t: Date.now(),
        s: optimizedState
      };
      
      // Use pako for maximum compression
      const json = JSON.stringify(payload);
      const compressed = pako.deflate(json, { level: 9 });
      const base64 = uint8ToBase64(compressed);
      
      // Create URL with compressed data
      const baseUrl = window.location.origin + window.location.pathname;
      return `${baseUrl}?s=${base64}`;
    } catch (error) {
      console.error('Failed to create shareable URL:', error);
      return window.location.href;
    }
  },
  
  /**
   * Loads shared state from a URL parameter
   * @returns true if state was successfully loaded
   */
  loadSharedState(): boolean {
    try {
      // Extract the share parameter from URL
      const urlParams = new URLSearchParams(window.location.search);
      const shareParam = urlParams.get('s');
      const strategyVersion = urlParams.get('sv'); // Get strategy version if provided
      
      if (!shareParam) {
        return false;
      }
      
      // Validate the share parameter
      if (!shareParam || shareParam.length === 0) {
        console.error('Share parameter is empty');
        return false;
      }
      
      // Decompress and parse the data
      let compressed: Uint8Array;
      try {
        compressed = base64ToUint8Array(shareParam);
      } catch (decodeError) {
        console.error('Failed to decode base64:', decodeError);
        // Clean URL and return false
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
        return false;
      }
      
      const json = pako.inflate(compressed, { to: 'string' });
      const payload = JSON.parse(json) as SharePayload;
      
      // Check version - if strategyVersion is 2, skip migration regardless of payload.v
      const strategyVersionNum = strategyVersion ? parseInt(strategyVersion, 10) : undefined;
      const isV1 = strategyVersionNum !== 2 && (!payload.v || payload.v === 1);
      const isV2 = strategyVersionNum === 2 || payload.v === 2;
      const migrationStats = { 
        mapped: 0, 
        unmapped: 0, 
        unmappedIds: [] as number[],
        mappings: [] as Array<{oldId: number, newId: number, oldDesc: string, newDesc: string}>
      };
      
      const state = payload.s;
      
      // Restore and apply the shared state to localStorage
      if (state.a) {
        localStorage.setItem('atlasDeviceState', JSON.stringify(restoreAtlasDeviceState(state.a as OptimizedAtlasState)));
      }
      
      if (state.b) {
        localStorage.setItem('atlasStrategy', JSON.stringify(restoreStrategy(state.b as OptimizedStrategyState)));
      }
      
      if (state.c) {
        // Migrate idol items only if from v1, v2 already has correct IDs
        const idolItems = isV1 ? migrateIdolItems(state.c as OptimizedIdolItem[], migrationStats) : (state.c as OptimizedIdolItem[]);
        localStorage.setItem('idolPlannerItems', JSON.stringify(restoreIdolItems(idolItems)));
      }
      
      if (state.d) {
        // Migrate stash items only if from v1, v2 already has correct IDs
        const stashItems = isV1 ? migrateIdolItems(state.d as OptimizedIdolItem[], migrationStats) : (state.d as OptimizedIdolItem[]);
        localStorage.setItem('idolStashItems', JSON.stringify(restoreIdolItems(stashItems)));
      }
      
      if (state.e) {
        localStorage.setItem('pinnedContentType', JSON.stringify(state.e));
      }
      
      if (state.f) {
        localStorage.setItem('pinnedContentTypes', JSON.stringify(state.f));
      }
      
      // Log migration summary if v1
      if (isV1 && (migrationStats.mapped > 0 || migrationStats.unmapped > 0)) {
        // eslint-disable-next-line no-console
        console.log(`✓ Imported 1.0 strategy: ${migrationStats.mapped} modifiers mapped, ${migrationStats.unmapped} not mapped`);
      } else if (isV2) {
        // eslint-disable-next-line no-console
        console.log('✓ Imported 2.0 strategy (no migration needed)');
      }
      
      // Clean URL by removing the share parameter
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
      
      return true;
    } catch (error) {
      console.error('Failed to load shared state:', error);
      // Clean URL even on error
      try {
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
      } catch (historyError) {
        console.error('Failed to clean URL:', historyError);
      }
      return false;
    }
  },
  
  /**
   * Clears all shareable state from localStorage
   */
  clearSharedState(): void {
    STORAGE_KEYS.forEach(key => {
      localStorage.removeItem(key);
    });
  },
  
  /**
   * Calculates the estimated size of the share URL
   */
  getShareUrlStats() {
    try {
      // Collect and optimize state just like we would for sharing
      const optimizedState: Record<string, unknown> = {};
      
      for (const key of STORAGE_KEYS) {
        const value = localStorage.getItem(key);
        if (!value) continue;
        
        try {
          if (key === 'atlasDeviceState') {
            optimizedState.a = optimizeAtlasDeviceState(value);
          } else if (key === 'atlasStrategy') {
            optimizedState.b = optimizeStrategy(value);
          } else if (key === 'idolPlannerItems') {
            optimizedState.c = optimizeIdolItems(value);
          } else if (key === 'idolStashItems') {
            optimizedState.d = optimizeIdolItems(value);
          } else if (key === 'pinnedContentType') {
            optimizedState.e = JSON.parse(value);
          } else if (key === 'pinnedContentTypes') {
            optimizedState.f = JSON.parse(value);
          }
        } catch (err) {
          console.error(`Failed to process ${key}:`, err);
        }
      }
      
      // Create the same payload we would share
      const payload: SharePayload = {
        v: 2, // Updated to version 2
        t: Date.now(),
        s: optimizedState
      };
      
      // Measure the different stages
      const json = JSON.stringify(payload);
      const jsonSize = json.length;
      
      const compressed = pako.deflate(json, { level: 9 });
      const compressedSize = compressed.length;
      
      const base64 = uint8ToBase64(compressed);
      const base64Size = base64.length;
      
      const estimatedUrlSize = window.location.pathname.length + 3 + base64Size;
      
      return {
        jsonSize,
        compressedSize,
        base64Size,
        estimatedUrlSize,
        compressionRatio: (compressedSize / jsonSize * 100).toFixed(1) + '%',
        idolCount: ((optimizedState.c as OptimizedIdolItem[] | undefined)?.length || 0) + 
          ((optimizedState.d as OptimizedIdolItem[] | undefined)?.length || 0)
      };
    } catch (error) {
      console.error('Error calculating share URL stats:', error);
      return {
        jsonSize: 0,
        compressedSize: 0,
        base64Size: 0,
        estimatedUrlSize: 0,
        compressionRatio: '0%',
        idolCount: 0
      };
    }
  }
};

export default ShareStateService;